/**
 * What the cursor is allowed to say — ROADMAP 9.9.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS DECIDES HIGHLIGHTING. THE SERVER DECIDES THE MOVE.
 *
 * It is a deliberate, stated duplicate of the rules in
 * `legTransferPolicy.ts`, and it exists so a trader dragging a NIFTY leg over
 * a BANKNIFTY basket sees it stay grey instead of learning on the drop. That
 * is the whole job.
 *
 * It is NOT a permission check and must never become one. The drawer's
 * numbers are from its last read: between this render and the drop a stop can
 * fire, an exit can be dispatched from another tab, or the reconciler can
 * flag the leg. Anything decided here is an opinion about a book that has
 * already moved on — the same hazard `exitPlan` refuses to take from the
 * browser, which is why the server re-reads inside its transaction and is
 * free to refuse what this permitted.
 *
 * The rules that need a server read are therefore ABSENT rather than
 * approximated: whole-held quantity, orphan flags and simulation scope are
 * not checked here. A drop this allows and the server refuses shows a
 * message; a drop this forbids wrongly is invisible and unexplainable, so
 * when in doubt this says yes.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { isGrouped, type OpenBookTrade } from './useOpenBook'

/** The leg under the cursor, captured at `dragstart`. */
export interface DraggedLeg {
  readonly tradeLegId: string
  readonly tradeId: string
  /**
   * Is the leg CURRENTLY in a group? 4.18.
   *
   * Was `strategyId`, which asked the same thing through a proxy that a
   * straddle placed from the chain breaks — it has no strategy and is a
   * group. Captured as the answer rather than the input, because the rules
   * below only ever asked the question.
   */
  readonly grouped: boolean
  readonly underlying: string
  readonly connectionId: string
  /** The contract. Null when the listing has no token, which cannot be moved. */
  readonly token: number | null
  /** Signed UNITS held. Its sign is what the opposite-contract rule needs. */
  readonly netQty: number
  readonly symbol: string
}

/** Baskets that can take a leg at all. Mirrors the server's `MOVEABLE`. */
const RECEPTIVE = new Set(['OPEN', 'ISSUE'])

/**
 * Could this leg plausibly join that basket?
 *
 * Expiry is deliberately not a rule — a leg carries its own, and a basket
 * spanning two is allowed. What it costs is the payoff curve, which refuses
 * to plot more than one expiry, and that is a consequence rather than a
 * prohibition.
 */
export function canAdopt(dragged: DraggedLeg, target: OpenBookTrade): boolean {
  if (dragged.token === null) return false
  // Where it already is. Not an error, just not a move.
  if (target.tradeId === dragged.tradeId) return false
  // Out of a basket or into one, never straight across. The server says the
  // same thing with a sentence explaining how to do it in two steps.
  if (dragged.grouped) return false
  if (!isGrouped(target)) return false
  if (!RECEPTIVE.has(target.status)) return false
  if (target.underlying !== dragged.underlying) return false
  if (target.broker.connectionId !== dragged.connectionId) return false

  /*
   * The opposite of a contract the basket already holds nets to flat at the
   * broker: one row for two of our legs, which is the grain mismatch behind
   * the exit planner's sign error, the orphan detector's false positives and
   * the exit quantity cap. The SAME direction is fine and stays two legs.
   */
  return !target.legs.some(
    (l) => l.token === dragged.token && l.netQty !== 0 && Math.sign(l.netQty) !== Math.sign(dragged.netQty),
  )
}

/** Can this leg be pulled out of its basket and back onto the manual list? */
export function canDetach(dragged: DraggedLeg): boolean {
  return dragged.grouped && dragged.token !== null
}
